/** Draws a running cave into the virtual screen (docs/spec/04 §4-5). */

import type { CaveState } from '../sim/types';
import { DX, DY } from '../sim/types';
import { cellLook } from './cellSprite';
import type { Cover } from './cover';
import type { Colour } from './font';
import { css } from './font';
import type { Screen } from './screen';
import type { SpriteSheet } from './sheet';
import { VARIANT_SPRITES } from './sheet';
import { hash } from './sprites/types';
import { CELL } from './sprites/types';

export interface CaveDrawOptions {
  /** Where the playfield starts on the screen, and its size in pixels. */
  readonly top: number;
  readonly viewW: number;
  readonly viewH: number;
  /** The camera's top-left in cave pixels (plus any shake). */
  readonly camX: number;
  readonly camY: number;
  /** 0..1 through the current tick: how far moved objects have slid (smooth motion only). */
  readonly t: number;
  readonly smooth: boolean;
  /** The 25 Hz animation clock. */
  readonly anim: number;
  readonly cover: Cover;
  readonly background: Colour;
  /** Space sparkles (a bonus life is being celebrated). */
  readonly sparkle: boolean;
}

/** Digby's drawn position in cave pixels, slid along his last move when smooth. */
export function digbyPixel(s: CaveState, t: number, smooth: boolean): { x: number; y: number } {
  const i = s.digby.y * s.w + s.digby.x;
  const dir = s.moved[i] ?? 0;
  const back = smooth && s.digby.moving ? 1 - t : 0;
  return { x: (s.digby.x - (DX[dir] ?? 0) * back) * CELL, y: (s.digby.y - (DY[dir] ?? 0) * back) * CELL };
}

export function drawCave(screen: Screen, s: CaveState, atlas: SpriteSheet, o: CaveDrawOptions): void {
  const ctx = screen.ctx;
  ctx.save();
  ctx.beginPath();
  ctx.rect(0, o.top, o.viewW, o.viewH);
  ctx.clip();
  ctx.fillStyle = css(o.background);
  ctx.fillRect(0, o.top, o.viewW, o.viewH);

  // One cell of margin all round, so things sliding in from outside the view are drawn.
  const x0 = Math.max(0, Math.floor(o.camX / CELL) - 1);
  const y0 = Math.max(0, Math.floor(o.camY / CELL) - 1);
  const x1 = Math.min(s.w - 1, Math.floor((o.camX + o.viewW - 1) / CELL) + 1);
  const y1 = Math.min(s.h - 1, Math.floor((o.camY + o.viewH - 1) / CELL) + 1);
  const sparkleColour = css(atlas.colors[2]);

  // Two passes: everything still, then everything that moved this tick, so movers draw on top.
  for (let pass = 0; pass < 2; pass++) {
    for (let y = y0; y <= y1; y++) {
      for (let x = x0; x <= x1; x++) {
        const i = y * s.w + x;
        const dir = o.smooth ? (s.moved[i] ?? 0) : 0;
        if ((pass === 0) !== (dir === 0)) continue;
        const sx = x * CELL - o.camX;
        const sy = y * CELL - o.camY + o.top;
        if (o.cover.active && o.cover.isCovered(x, y)) {
          if (pass === 0) atlas.draw(ctx, 'cover', o.anim + (hash(x, y, 1) % 4), sx, sy);
          continue;
        }
        const look = cellLook(s.cells[i] ?? 0, s, o.anim);
        if (look.sprite === null) {
          if (o.sparkle && hash(x, y, o.anim >> 1) % 5 === 0) {
            ctx.fillStyle = sparkleColour;
            ctx.fillRect(sx + (hash(x, y, o.anim) % 7) * 2, sy + (hash(y, x, o.anim) % 15), 2, 1);
          }
          continue;
        }
        const back = dir === 0 ? 0 : 1 - o.t;
        const ox = Math.round(-(DX[dir] ?? 0) * back * CELL);
        const oy = Math.round(-(DY[dir] ?? 0) * back * CELL);
        // Where the sheet has variants (the remastered one): dirt is a 4 x 4-cell seamless texture,
        // brick and steel pick one of four at random per cell. The C64 sheet has one of each.
        let frame = look.frame;
        if (look.sprite === 'dirt') frame = ((y & 3) << 2) | (x & 3);
        else if (VARIANT_SPRITES.has(look.sprite)) frame = hash(x, y, 17) & 3;
        atlas.draw(ctx, look.sprite, frame, sx + ox, sy + oy);
      }
    }
  }
  ctx.restore();
}
